import React, { type MouseEvent, type ReactElement, useEffect, useMemo, useState } from 'react';

import formatISO from 'date-fns/formatISO';
import isValid from 'date-fns/fp/isValid';

import useTranslation from '../../features/locale/useTranslation';
import { DEFAULT_DATE_FORMAT, devError } from '../../utils';
import {
    Calendar,
    type CalendarChangeEvent,
    type CalendarChangeValue,
    type CalendarProps,
    DEFAULT_CHANGE_EVENT
} from '../Calendar';
import { type InputBaseProps } from '../Input';

import { DatePickerCalendarCustomInput } from './components/DatePickerCalendarCustomInput';

export type DatePickerValue = string | null;
export type DatePickerParsedValue = Date | null;
export type DatePickerValueRange = [DatePickerValue, DatePickerValue];
export type DatePickerValueSingle = DatePickerValue;
export type DatePickerChangeEvent = CalendarChangeEvent;

type FormatISOOption = {
    format?: 'extended' | 'basic' | undefined;
    representation?: 'complete' | 'date' | 'time' | undefined;
};

type DatePickerBaseProps = {
    /**
     * Date format to display in input
     */
    dateFormat?: string;
    /**
     * Props for `Calendar` component
     */
    calendarProps?: Omit<
        CalendarProps,
        'customInput' | 'selectsRange' | 'value' | keyof DatePickerProps
    > & {
        autoComplete?: 'off' | 'on';
        id?: string;
        popperPlacement?: string;
        popperModifiers?: any[]; // not exposed by react-datepicker
    } & {
        showPreviousMonths?: boolean;
    };
    /**
     * The earliest date that the user can select.
     */
    minDate?: Date | null;
    /**
     * The latest date that the user can select.
     */
    maxDate?: Date | null;
    /**
     * If `true`, the user will not be able to interact with the input.
     */
    disabled?: boolean;
    /**
     * A string that will be used as a placeholder for the input.
     */
    placeholder?: string;
    /**
     * Props for customize the date output format
     */
    dateOutputFormat?: FormatISOOption;
};

type DatePickerSingleProps = {
    showSelectRangeValue?: boolean;
    /**
     * Enable/disable the ability to select a range of dates.
     */
    selectsRange?: false;
    /**
     * The date as a string value [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) standard in the `YYYY-MM-DD` format.
     * - If `selectsRange` is `true`, the value should be an array of two dates (e.g. `['2021-01-01', '2021-01-31']`).
     * - If `selectsRange` is `false`, the value should be a string with a single date (e.g. `2021-01-01`).
     */
    value?: DatePickerValueSingle;
    onChange?: (value: DatePickerValue, event: DatePickerChangeEvent) => void;
} & DatePickerBaseProps;

type DatePickerRangeProps = {
    /**
     * Enable/disable the ability to select a range of dates.
     */
    selectsRange: true;
    showSelectRangeValue?: boolean;
    value?: DatePickerValueRange;
    onChange?: (value: [DatePickerValue, DatePickerValue], event: DatePickerChangeEvent) => void;
} & DatePickerBaseProps;

type DatePickerUnionProps = DatePickerSingleProps | DatePickerRangeProps;

export type DatePickerProps = DatePickerUnionProps &
    Omit<InputBaseProps, keyof DatePickerUnionProps>;

const parseDatePickerInput = (
    value: DatePickerValueRange | DatePickerValueSingle | undefined
): [DatePickerParsedValue, DatePickerParsedValue] => {
    const [startDateString, endDateString] = Array.isArray(value) ? value : [value, null];
    if (!startDateString) {
        return [null, null];
    }
    let startDate: Date | null = startDateString ? new Date(startDateString) : null;
    let endDate: Date | null = endDateString ? new Date(endDateString) : null;

    if (startDate) {
        if (!isValid(startDate)) {
            devError(`You provided an invalid start date value: ${startDateString}`);
            startDate = null;
        }
    }

    if (endDate) {
        if (!isValid(endDate)) {
            devError(`You provided an invalid end date value: ${endDateString}`);
            endDate = null;
        }
    }

    return [startDate, endDate];
};

const toDatePickerOutputValue = (
    value: CalendarChangeValue,
    dateOutputFormat?: FormatISOOption
): DatePickerValue | [DatePickerValue, DatePickerValue] => {
    if (!value) {
        return null;
    }
    if (Array.isArray(value)) {
        return value.map((v) => (v ? formatISO(v, dateOutputFormat ?? {}) : null)) as [
            DatePickerValue,
            DatePickerValue
        ];
    }
    return formatISO(value, dateOutputFormat ?? {});
};

export const DatePicker = ({
    className,
    placeholder,
    disabled,
    readOnly,
    value,
    onChange,
    dateFormat = DEFAULT_DATE_FORMAT,
    dateOutputFormat,
    minDate,
    maxDate,
    calendarProps,
    selectsRange,
    showSelectRangeValue,
    onMouseDown,
    ...restInputProps
}: DatePickerProps): ReactElement => {
    const { t } = useTranslation();
    const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
    const mergedPlaceholder = readOnly && !value ? t('None') : placeholder;
    const [startDate, endDate] = useMemo(() => parseDatePickerInput(value), [value]);

    const handleInputMouseDown = (event: MouseEvent<HTMLInputElement>) => {
        setCalendarOpen(true);
        onMouseDown?.(event);
    };

    const handleCalendarOpen = () => {
        setCalendarOpen(true);
        calendarProps?.onOpen?.();
    };

    useEffect(() => {
        if (typeof calendarProps?.open === 'boolean') {
            setCalendarOpen(calendarProps.open);
        }
    }, [calendarProps?.open]);

    const handleCalendarChange = (date: CalendarChangeValue, event: CalendarChangeEvent) => {
        const outputValue = toDatePickerOutputValue(date, dateOutputFormat);
        onChange?.(outputValue as never, event);
    };

    const handleClear = () => {
        handleCalendarChange(selectsRange ? [null, null] : null, DEFAULT_CHANGE_EVENT);
    };

    const handleCalendarClose = () => {
        setCalendarOpen(false);
        calendarProps?.onClose?.();
    };

    return (
        <Calendar
            {...calendarProps}
            placeholder={mergedPlaceholder}
            selected={startDate}
            startDate={startDate}
            endDate={endDate}
            minDate={minDate}
            maxDate={maxDate}
            dateFormat={dateFormat}
            selectsRange={selectsRange}
            onChange={handleCalendarChange}
            open={calendarOpen && !readOnly && !disabled}
            readOnly={readOnly}
            onClose={handleCalendarClose}
            onOpen={handleCalendarOpen}
            disabled={disabled}
            customInput={
                <DatePickerCalendarCustomInput
                    {...restInputProps}
                    selectsRange={selectsRange}
                    showSelectRangeValue={showSelectRangeValue}
                    onMouseDown={handleInputMouseDown}
                    readOnly={readOnly}
                    value={value as string}
                    className={className}
                    onClear={handleClear}
                />
            }
        />
    );
};
